import React, { useState } from "react";
import { Button, Form, Input, message } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { dashboard } from "../../../urls";
import { loginToBothFirebase, logoutFromBothFirebase, auth } from "../../../configs/FirebaseConfig";
import roles from "../../../roles";
import { vceFirestore } from "../../../configs/FirebaseConfig";

export const LoginForm = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showForgetPassword, onForgetPasswordClick, extra } = props;
  const initialCredentials = { email: "", password: "" };
  const [loginEmail, setLoginEmail] = useState(initialCredentials.email);
  const [loginPassword, setLoginPassword] = useState(initialCredentials.password);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const sendVerificationMail = () => {
    auth.currentUser
      .sendEmailVerification()
      .then(() =>
        message.success({
          content: t("notifications.invite_link_sent"),
          duration: 8,
        }),
      )
      .catch((err) => message.warning(err, 8));
  };

  const onLogin = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      // Login to both VCE and HVO Firebase instances
      const { vceUser, hvoUser } = await loginToBothFirebase(loginEmail, loginPassword);

      // Check if user is a CMS user
      const userDoc = await vceFirestore.collection("users").doc(vceUser.user.uid).get();
      const role = userDoc.data()?.role;

      if (roles.CMS_ROLES.includes(role)) {
        // Log success messages
        if (hvoUser) {
          message.success("Ingelogd op beide systemen", 2);
        } else {
          message.info("Ingelogd op VCE (HVO account niet gevonden)", 2);
        }
        navigate(dashboard);
      } else {
        // Signed in, but not allowed in: sign out again, or the session stays and the auth pages
        // bounce the account straight into the CMS on the next render
        await logoutFromBothFirebase();
        setIsLoading(false);
        setErrorMessage(role === roles.picker ? t("notifications.picker_login_refused") : t("notifications.unauthorized_user"));
      }
    } catch (err) {
      setIsLoading(false);
      message.warning(err.message);
      setErrorMessage(err.code);
    }
  };

  return (
    <>
      <Form layout="vertical" name="login-form" onFinish={onLogin} className="mt-4" initialValues={initialCredentials}>
        <Form.Item
          name="email"
          label={t("email")}
          onChange={(e) => setLoginEmail(e.target.value)}
          rules={[
            {
              required: true,
              message: t("form.enter_email"),
            },
            {
              type: "email",
              message: t("form.enter_valid_email"),
            },
          ]}
        >
          <Input prefix={<MailOutlined className="text-primary" />} />
        </Form.Item>
        <Form.Item
          name="password"
          onChange={(e) => setLoginPassword(e.target.value)}
          label={
            <div className={`${showForgetPassword ? "d-flex justify-content-between w-100 align-items-center" : ""}`}>
              <span>{t("password")}</span>
              {showForgetPassword && (
                <span
                  onClick={() => onForgetPasswordClick}
                  className="cursor-pointer font-size-sm font-weight-normal text-muted"
                >
                  {t("forgot_password")}
                </span>
              )}
            </div>
          }
          rules={[
            {
              required: true,
              message: t("form.enter_password"),
            },
          ]}
        >
          <Input.Password prefix={<LockOutlined className="text-primary" />} />
        </Form.Item>
        <div className="w-100 text-center mb-2">
          <span className="text-danger">{errorMessage}</span>
        </div>
        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={isLoading}>
            {t("login")}
          </Button>
        </Form.Item>
        {extra}
      </Form>
    </>
  );
};

LoginForm.propTypes = {
  showForgetPassword: PropTypes.bool,
  extra: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

LoginForm.defaultProps = {
  showForgetPassword: false,
};

export default LoginForm;
